/**
 * Parça Takip & İnceleme — tip tanımları.
 * Parça = paylaşım sisteminde bir ANA KART (IKart, turu KART). Her aksiyon = ALT KART (_uid = parçaId).
 * Veri sunucuda (hareketler koleksiyonu) — dosyada değil. STP fingerprint ile tanıma.
 */

/** Üretim → sevkiyat yaşam döngüsü aşamaları (sıralı). */
export type ParcaAsama =
  | 'Teklif' | 'Onay' | 'Malzeme' | 'Üretim' | 'Kalite' | 'Sevkiyat' | 'Teslim'
  | 'Red' | 'İptal';

export const PARCA_ASAMALAR: ParcaAsama[] = ['Teklif', 'Onay', 'Malzeme', 'Üretim', 'Kalite', 'Sevkiyat', 'Teslim'];

export interface IAsamaBilgi { renk: string; rgb: string; simge: string; ilerleme: number; }
export const ASAMA_BILGI: Record<ParcaAsama, IAsamaBilgi> = {
  'Teklif':   { renk: '#94a3b8', rgb: '148,163,184', simge: '💰', ilerleme: 10 },
  'Onay':     { renk: '#3b82f6', rgb: '59,130,246',  simge: '✅', ilerleme: 25 },
  'Malzeme':  { renk: '#06b6d4', rgb: '6,182,212',   simge: '📦', ilerleme: 40 },
  'Üretim':   { renk: '#a855f7', rgb: '168,85,247',  simge: '🏭', ilerleme: 60 },
  'Kalite':   { renk: '#eab308', rgb: '234,179,8',   simge: '🔍', ilerleme: 80 },
  'Sevkiyat': { renk: '#f97316', rgb: '249,115,22',  simge: '🚚', ilerleme: 92 },
  'Teslim':   { renk: '#22c55e', rgb: '34,197,94',   simge: '🎯', ilerleme: 100 },
  'Red':      { renk: '#ef4444', rgb: '239,68,68',   simge: '⛔', ilerleme: 0 },
  'İptal':    { renk: '#434343', rgb: '67,67,67',    simge: '✖', ilerleme: 0 },
};

/** Alt kart (aksiyon) tipleri — her biri ayrı dinamik bileşen id'si. */
export type ParcaAltTip = 'NOT' | 'OLCU' | 'AGIRLIK' | 'DURUM' | 'ONAY' | 'TEKLIF' | 'FOTO' | 'ODEME' | 'DOSYA' | 'MONAY';

export interface IAltTipBilgi { id: string; baslik: string; simge: string; renk: string; }
/** Alt kart tipi → dinamik bileşen id'si ('parca-' + lowercase) + UI bilgisi. */
export const ALT_TIP_BILGI: Record<ParcaAltTip, IAltTipBilgi> = {
  NOT:     { id: 'parca-not',     baslik: 'Not',      simge: '📝', renk: '#38bdf8' },
  OLCU:    { id: 'parca-olcu',    baslik: 'Ölçü',     simge: '📏', renk: '#a78bfa' },
  AGIRLIK: { id: 'parca-agirlik', baslik: 'Ağırlık',  simge: '⚖️', renk: '#34d399' },
  DURUM:   { id: 'parca-durum',   baslik: 'Durum',    simge: '🔄', renk: '#3b82f6' },
  ONAY:    { id: 'parca-onay',    baslik: 'Onay',     simge: '✅', renk: '#22c55e' },
  TEKLIF:  { id: 'parca-teklif',  baslik: 'Teklif',   simge: '💰', renk: '#f59e0b' },
  FOTO:    { id: 'parca-foto',    baslik: 'Fotoğraf', simge: '📷', renk: '#fb7185' },
  ODEME:   { id: 'parca-odeme',   baslik: 'Tahsilat', simge: '💵', renk: '#16a34a' },
  DOSYA:   { id: 'parca-dosya',   baslik: 'Dosya',    simge: '📎', renk: '#64748b' },
  MONAY:   { id: 'parca-monay',   baslik: 'Müşteri Kararı', simge: '🤝', renk: '#8b5cf6' },
};

/** Dinamik bileşen id'si (ana parça kartı). */
export const PARCA_KART_BILESEN = 'parca-takip';
/** Parametre seti / içerik komutu. */
export const HAREKETLER = {
  aggregate: 'FikoYT.sistem.DB.hareketler.aggregate',
  ekle: 'FikoYT.sistem.DB.hareketler.ekle',
  guncelle: 'FikoYT.sistem.DB.hareketler.guncelle',
  sil: 'FikoYT.sistem.DB.hareketler.sil',
} as const;

/** 3D'de noktaya bağlı not (LOCAL koordinat). */
export interface IParcaNot { x: number; y: number; z: number; metin: string; }

/** STP geometrik parmak izi (tanıma için). */
export interface IParcaFingerprint {
  fp: string;          // birleşik imza: f{faces}_e{edges}_v{vol}
  faceSayisi: number;
  edgeSayisi: number;
  hacim: number;       // mm³ (teorik), yoksa bbox hacmi
  bbox?: { x: number; y: number; z: number };
}

/** Parça ana kartı veri yapısı (IKart.veri içinde). */
export interface IParcaVeri {
  parcaAdi: string;
  revizyon?: string;
  malzeme?: string;
  yogunluk?: number;       // g/cm³
  teorikAgirlik?: number;  // kg
  olculenAgirlik?: number; // kg
  asama: ParcaAsama;
  hedefTarih?: string;     // hedef teslim tarihi (gecikme uyarısı için)
  oncelik?: 'normal' | 'acil';   // öncelik bayrağı
  adet?: number;           // sipariş adedi
  malzemeBirimFiyat?: number;    // ₺/kg (maliyet hesabı)
  iscilik?: number;        // ₺ (işçilik+genel gider)
  musteriAdi?: string;     // cari/müşteri adı
  musteriTel?: string;     // müşteri telefonu (WhatsApp doğrudan gönderim)
  fingerprint?: IParcaFingerprint;
  stpDosyaId?: string;     // go_files referansı (ileride)
  notlar?: IParcaNot[];
  kok?: boolean;           // ana (kök) parça kartı işareti — liste sorgusu sağlamlığı
  firmaId?: string;        // ait olduğu firma (çoklu firma izolasyonu) — yoksa kişisel (sahip bazlı)
  paylasimToken?: string;  // müşteri public link token'ı (loginsiz durum sayfası)
  tokenAktif?: boolean;    // link aktif mi (firma kapatabilir)
  tahminiTeslim?: string;  // müşteriye gösterilen tahmini teslim
}
